// Pregnancy dating and obstetric history (docs/plans/phase2-maternity-mch.md §4.1).
//
// TODO(clinical-verify) M1: the EDD is the standard calendar rule (first day of the last
// menstrual period + 280 days), with no cycle-length adjustment, and gestational age is the
// days since the LMP (or 280 minus the days to an EDD entered from elsewhere, such as a
// scan). Gestational age is only ever shown as weeks and days: nothing here classifies a
// pregnancy as preterm, term or post-dates, because those are clinical thresholds.

import { err, ok, type Result } from '../result.ts';
import {
  addDays,
  compareDays,
  daysBetween,
  formatIsoDay,
  parseIsoDay,
  type CalendarDay,
} from '../time.ts';

/** Days from the LMP to the EDD under the standard calendar rule. TODO(clinical-verify) M1. */
export const PREGNANCY_DAYS_FROM_LMP = 280;

export const EDD_BASES = ['lmp', 'entered'] as const;
export type EddBasis = (typeof EDD_BASES)[number];

export const EDD_BASIS_LABELS: Readonly<Record<EddBasis, string>> = {
  lmp: 'From the LMP',
  entered: 'Entered (for example from a scan)',
};

export function eddFromLmp(lmp: CalendarDay): CalendarDay {
  return addDays(lmp, PREGNANCY_DAYS_FROM_LMP);
}

export interface Gestation {
  readonly weeks: number;
  readonly days: number;
  readonly totalDays: number;
}

export interface PregnancyDates {
  readonly lmp: CalendarDay | null;
  readonly edd: CalendarDay | null;
  readonly eddBasis?: EddBasis | null;
}

/**
 * Gestational age on a day: from the LMP, unless the EDD was entered from elsewhere (then
 * counted back from it). Null with no dates, or on a day before the pregnancy began.
 */
export function gestationOn(dates: PregnancyDates, on: CalendarDay): Gestation | null {
  let totalDays: number | null = null;
  if (dates.edd !== null && (dates.eddBasis === 'entered' || dates.lmp === null)) {
    totalDays = PREGNANCY_DAYS_FROM_LMP - daysBetween(on, dates.edd);
  } else if (dates.lmp !== null) {
    totalDays = daysBetween(dates.lmp, on);
  }
  if (totalDays === null || totalDays < 0) return null;
  return { weeks: Math.floor(totalDays / 7), days: totalDays % 7, totalDays };
}

/**
 * The banner's passive "Past EDD, check pregnancy status" cue (plan M23): true only when the
 * facility has set a number of weeks and today is more than that many weeks past the EDD.
 * TODO(clinical-verify) M23: the number of weeks is a setting, empty by default, so there is
 * no cue until the clinical lead sets one. A reminder to update the record, not a judgement.
 */
export function isPastEddCue(
  edd: CalendarDay | null,
  today: CalendarDay,
  weeksAfterEdd: number | null,
): boolean {
  if (edd === null || weeksAfterEdd === null) return false;
  return daysBetween(edd, today) > weeksAfterEdd * 7;
}

/** "24 weeks 3 days", "1 week 1 day", "30 weeks". */
export function formatGestation(gestation: Gestation): string {
  const weeks = `${String(gestation.weeks)} ${gestation.weeks === 1 ? 'week' : 'weeks'}`;
  if (gestation.days === 0) return weeks;
  return `${weeks} ${String(gestation.days)} ${gestation.days === 1 ? 'day' : 'days'}`;
}

export type PregnancyDatesErrorCode =
  | 'pregnancy.date_invalid'
  | 'pregnancy.lmp_in_future'
  | 'pregnancy.edd_basis_required'
  | 'pregnancy.edd_not_from_lmp'
  | 'pregnancy.edd_before_lmp';

export interface CheckedPregnancyDates {
  readonly lmp: string | null;
  readonly edd: string | null;
  readonly eddBasis: EddBasis | null;
}

/**
 * The dates as they will be stored. An LMP with no EDD gets the calculated EDD; an EDD needs
 * its basis; an EDD "from the LMP" must be the calculated one. No other limits (M8).
 */
export function checkPregnancyDates(input: {
  lmp: string | null;
  edd: string | null;
  eddBasis: EddBasis | null;
  today: CalendarDay;
}): Result<CheckedPregnancyDates, PregnancyDatesErrorCode> {
  const lmp = input.lmp === null ? null : parseIsoDay(input.lmp);
  const edd = input.edd === null ? null : parseIsoDay(input.edd);
  if ((lmp !== null && !lmp.ok) || (edd !== null && !edd.ok)) return err('pregnancy.date_invalid');
  const lmpDay = lmp?.value ?? null;
  const eddDay = edd?.value ?? null;
  if (lmpDay !== null && compareDays(lmpDay, input.today) > 0) {
    return err('pregnancy.lmp_in_future');
  }
  if ((eddDay === null) !== (input.eddBasis === null)) {
    // An EDD with no basis, or a basis with nothing to describe, unless the LMP gives both.
    if (!(eddDay === null && input.eddBasis === null)) return err('pregnancy.edd_basis_required');
  }
  if (eddDay === null) {
    if (lmpDay === null) return ok({ lmp: null, edd: null, eddBasis: null });
    return ok({ lmp: input.lmp, edd: formatIsoDay(eddFromLmp(lmpDay)), eddBasis: 'lmp' });
  }
  if (input.eddBasis === 'lmp') {
    if (lmpDay === null || compareDays(eddFromLmp(lmpDay), eddDay) !== 0) {
      return err('pregnancy.edd_not_from_lmp');
    }
  }
  if (lmpDay !== null && compareDays(eddDay, lmpDay) <= 0) return err('pregnancy.edd_before_lmp');
  return ok({ lmp: input.lmp, edd: input.edd, eddBasis: input.eddBasis });
}

// --- Obstetric history ------------------------------------------------------------------------

/** Data-entry bound against typing errors, not a clinical limit (plan M7). */
export const OBSTETRIC_COUNT_MAX = 30;

export interface ObstetricHistory {
  readonly gravida: number | null;
  readonly parity: number | null;
  /** The number after the plus in "P1+1", as the handbook writes it. */
  readonly parityPlus: number | null;
}

export type ObstetricHistoryErrorCode = 'pregnancy.gravida_invalid' | 'pregnancy.parity_invalid';

const isCount = (n: number | null, min: number) =>
  n === null || (Number.isInteger(n) && n >= min && n <= OBSTETRIC_COUNT_MAX);

/** Whole numbers within the entry bounds. Recorded as entered: no consistency rule. */
export function checkObstetricHistory(history: ObstetricHistory): ObstetricHistoryErrorCode | null {
  if (!isCount(history.gravida, 1)) return 'pregnancy.gravida_invalid';
  if (!isCount(history.parity, 0) || !isCount(history.parityPlus, 0)) {
    return 'pregnancy.parity_invalid';
  }
  return null;
}

/** "G3 P1+1", "G2", or "Not recorded". */
export function formatObstetricHistory(history: ObstetricHistory): string {
  const parts: string[] = [];
  if (history.gravida !== null) parts.push(`G${String(history.gravida)}`);
  if (history.parity !== null || history.parityPlus !== null) {
    parts.push(`P${String(history.parity ?? 0)}+${String(history.parityPlus ?? 0)}`);
  }
  return parts.length === 0 ? 'Not recorded' : parts.join(' ');
}

/** Rule ID of the alert on opening a pregnancy, as logged in `clinical_alert_events`. */
export const PREGNANCY_SEX_ALERT_RULE = 'maternity.pregnancy_sex_not_female';

/** The sex code the provisional local value set uses for female (seed; TODO(verify-spec)). */
const FEMALE_SEX_CODE = 'female';

/**
 * Opening a pregnancy for a patient not recorded as female (plan M25): an interruptive alert,
 * acknowledged with a reason, never a block (the recorded sex may be wrong). TODO(clinical-verify)
 * M25: the tier, and matching on the provisional value set's code `female`.
 */
export function pregnancySexAlert(
  sexCode: string,
): { ruleId: typeof PREGNANCY_SEX_ALERT_RULE; tier: 'interruptive' } | null {
  if (sexCode === FEMALE_SEX_CODE) return null;
  return { ruleId: PREGNANCY_SEX_ALERT_RULE, tier: 'interruptive' };
}
